import React from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://businessdevelopmentauthority.net';

const MachineLearningPage: React.FC = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${SITE_URL}/machine-learning`,
        'url': `${SITE_URL}/machine-learning`,
        'name': 'Machine Learning Solutions - Business Development Authority',
        'description': 'Harness the power of machine learning to unlock insights, predict outcomes, and drive intelligent decision-making. Custom ML models for predictive analytics, NLP, computer vision, and more.',
        'isPartOf': { '@id': SITE_URL },
      },
      {
        '@type': 'Service',
        'name': 'Machine Learning Solutions',
        'url': `${SITE_URL}/machine-learning`,
        'description': 'Custom ML model development including predictive analytics, NLP, computer vision, recommendation systems, anomaly detection, and customer segmentation.',
        'serviceType': 'Machine Learning Consulting',
        'provider': {
          '@type': 'Organization',
          'name': 'Business Development Authority',
          'url': SITE_URL,
        },
        'areaServed': 'US',
        'hasOfferCatalog': {
          '@type': 'OfferCatalog',
          'name': 'Machine Learning Services',
          'itemListElement': [
            { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Predictive Analytics' } },
            { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Natural Language Processing' } },
            { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Computer Vision' } },
            { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Recommendation Systems' } },
            { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Anomaly Detection' } },
          ],
        },
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
      <div className="min-h-screen">
        <Header />
        
        {/* Hero Section */}
        <section className="relative min-h-[600px] flex items-center justify-center bg-cover bg-center" style={{ backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://public.readdy.ai/ai/img_res/d7428b06dab41eed770b8c74a54e18e5.jpg')` }}>
          <div className="max-w-7xl mx-auto px-6 text-center text-white w-full">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Machine Learning Solutions</h1>
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">Harness the power of machine learning to unlock insights, predict outcomes, and drive intelligent decision-making</p>
            <button onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })} className="whitespace-nowrap bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors cursor-pointer">
              Explore ML Solutions
            </button>
          </div>
        </section>

        {/* Overview Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold mb-6">Intelligent Systems That Learn and Adapt</h2>
                <p className="text-lg text-gray-700 mb-6">
                  Our Machine Learning Solutions empower businesses to extract valuable insights from data, automate complex decision-making, and create predictive models that drive competitive advantage. We develop custom ML models tailored to your specific business challenges.
                </p>
                <p className="text-lg text-gray-700 mb-6">
                  From predictive analytics to natural language processing, computer vision to recommendation systems, we deliver production-ready ML solutions that generate measurable business value.
                </p>
              </div>
              <div className="relative h-[400px] rounded-lg overflow-hidden shadow-2xl">
                <img src="https://readdy.ai/api/search-image?query=Data%20scientists%20working%20with%20machine%20learning%20models%20on%20large%20displays%20showing%20neural%20networks%20and%20data%20analytics%2C%20modern%20tech%20office%20with%20AI%20visualization%20dashboards%2C%20professional%20collaborative%20environment&width=600&height=400&seq=ml-overview&orientation=landscape" alt="Machine Learning" className="w-full h-full object-cover object-top" />
              </div>
            </div>
          </div>
        </section>

        {/* ML Solutions Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-center mb-12">Our ML Capabilities</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-600 rounded-lg mb-6">
                  <i className="ri-line-chart-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">Predictive Analytics</h3>
                <p className="text-gray-700">Forecast trends, customer behavior, and business outcomes with advanced predictive models and time series analysis.</p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-600 rounded-lg mb-6">
                  <i className="ri-chat-3-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">Natural Language Processing</h3>
                <p className="text-gray-700">Extract insights from text, automate document analysis, and build intelligent chatbots and virtual assistants.</p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-600 rounded-lg mb-6">
                  <i className="ri-eye-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">Computer Vision</h3>
                <p className="text-gray-700">Implement image recognition, object detection, and visual quality inspection systems for automated analysis.</p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-600 rounded-lg mb-6">
                  <i className="ri-star-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">Recommendation Systems</h3>
                <p className="text-gray-700">Build personalized recommendation engines that increase engagement and drive revenue growth.</p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-600 rounded-lg mb-6">
                  <i className="ri-shield-check-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">Anomaly Detection</h3>
                <p className="text-gray-700">Identify fraud, security threats, and operational anomalies in real-time with intelligent monitoring systems.</p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-600 rounded-lg mb-6">
                  <i className="ri-group-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">Customer Segmentation</h3>
                <p className="text-gray-700">Discover hidden patterns in customer data and create targeted segments for personalized marketing.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-center mb-12">Our ML Development Process</h2>
            <div className="grid md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-blue-600 rounded-full mx-auto mb-4">
                  <span className="text-3xl font-bold text-white">1</span>
                </div>
                <h3 className="text-xl font-bold mb-2">Data Assessment</h3>
                <p className="text-gray-700">Evaluate data quality, availability, and readiness for ML model development.</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-blue-600 rounded-full mx-auto mb-4">
                  <span className="text-3xl font-bold text-white">2</span>
                </div>
                <h3 className="text-xl font-bold mb-2">Model Development</h3>
                <p className="text-gray-700">Design, train, and optimize ML models using state-of-the-art algorithms and techniques.</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-blue-600 rounded-full mx-auto mb-4">
                  <span className="text-3xl font-bold text-white">3</span>
                </div>
                <h3 className="text-xl font-bold mb-2">Validation & Testing</h3>
                <p className="text-gray-700">Rigorously test models for accuracy, performance, and reliability before deployment.</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-blue-600 rounded-full mx-auto mb-4">
                  <span className="text-3xl font-bold text-white">4</span>
                </div>
                <h3 className="text-xl font-bold mb-2">Deployment & Monitoring</h3>
                <p className="text-gray-700">Deploy models to production and continuously monitor performance for optimal results.</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section id="contact" className="py-20 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-4xl font-bold mb-6">Ready to Leverage Machine Learning?</h2>
            <p className="text-xl mb-8">Let's discuss how ML can solve your business challenges and unlock new opportunities for growth.</p>
            <button onClick={() => window.REACT_APP_NAVIGATE('/calendar')} className="whitespace-nowrap bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-lg text-lg font-semibold transition-colors cursor-pointer">
              Schedule a Discovery Call
            </button>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default MachineLearningPage;
